import { forwardRef, useRef } from "react"
import cn from "classnames"

import style from "modules/PsyProfile/components/Booking/style.module.scss"
import { Text, Box, Img, Button } from "ui-kit/components-branded/index"
import { Psychologist } from "utils/interfaces"
import { ServicesGroup } from "utils/helpers/ServicesGroup"
import { CardImage } from "modules/BookingCommon/components/CardsPage/components/CardImage"
import { Icon, Price } from "components"
import { NewBookingPageLink } from "app/AppRouter/links"
import { APP_STATUSES } from "utils/AppStatusContext"
import { checkTimeRequestIfNoSlots, getLocationPath } from "utils/helpers"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { CURRENCY } from "utils/constants/currency"
import { useShowPrice } from "utils/hooks/useShowPrice"

interface Props {
    psy: Psychologist
    query: Record<string, string>
    position: string
    bookingAvailable: boolean
    serviceGroups: ServicesGroup[]
    selectGroup: ServicesGroup | undefined
    dateTestIdPrefix: string
    showStories?: boolean
    onChangeSelectedGroup: (value: ServicesGroup | undefined) => void
    onClickAskPsy: () => void
}
export const Booking = forwardRef<HTMLDivElement, Props>(
    (
        {
            psy,
            query,
            position,
            bookingAvailable,
            serviceGroups,
            selectGroup,
            dateTestIdPrefix,
            showStories,
            onChangeSelectedGroup,
            onClickAskPsy,
        },
        ref
    ) => {
        const showPrice = useShowPrice()
        const intersectionRef = useRef<HTMLDivElement | null>(null)
        const pathName = getLocationPath()
        const [{ userAccount }] = useClientContext()
        const hasSession = checkTimeRequestIfNoSlots(psy.hash_id, userAccount.psychologists)

        return (
            <Box
                className={cn(style.container, {
                    [style.isShowStories]: showStories,
                })}
            >
                <div className={style.list} ref={ref}>
                    <div ref={intersectionRef} className={style.items}>
                        {serviceGroups.map((group) => {
                            const selected = selectGroup?.id === group.id
                            const reasonSource = bookingAvailable
                                ? group.availableStatus
                                : group.availableStatusWithCompany
                            const available = reasonSource.ok

                            return (
                                <div
                                    className={cn(style.item, {
                                        [style.isSelected]: selected,
                                        [style.isDisabled]: !available,
                                    })}
                                    onClick={
                                        available ? () => onChangeSelectedGroup(group) : undefined
                                    }
                                    key={group.id}
                                    data-testid={`psy-profile-service-${group.id}-${dateTestIdPrefix}`}
                                >
                                    <div className={style.content}>
                                        <Text className={style.name}>{group.name}</Text>
                                        {selected && (
                                            <Icon
                                                type="circle-check"
                                                size={24}
                                                className={style.check}
                                            />
                                        )}
                                    </div>
                                    <CardImage serviceGroup={group} className={style.image} />
                                </div>
                            )
                        })}
                    </div>
                    {showPrice && (
                        <Text className={style.price}>
                            <Img className={style.priceEmoji} src="card-emoji.png" />
                            <span data-testid={`psy-profile-price-title-${dateTestIdPrefix}`}>
                                Стоимость&nbsp;
                            </span>
                            <Price
                                services={selectGroup?.services}
                                className="is-lowercase"
                                psy={psy}
                                currency={CURRENCY.RUB}
                                data-testid={`psy-profile-price-${dateTestIdPrefix}`}
                            />
                        </Text>
                    )}
                    {!bookingAvailable && (
                        <Text
                            className="block mb-0"
                            data-testid={`psy-profile-no-slots-${dateTestIdPrefix}`}
                        >
                            Нет свободных слотов
                        </Text>
                    )}

                    <NewBookingPageLink
                        query={query}
                        position={position}
                        hashId={psy.hash_id}
                        from={APP_STATUSES.profile}
                        className={cn(style.button, {
                            "button-primary": bookingAvailable,
                            "button-secondary": !bookingAvailable,
                        })}
                        data-testid={
                            bookingAvailable
                                ? `psy-profile-pick-time-${dateTestIdPrefix}`
                                : `psy-profile-request-time-${dateTestIdPrefix}`
                        }
                        fromLink={pathName}
                        state={{
                            serviceGroupId: selectGroup
                                ? selectGroup.services[0].service_type_id
                                : undefined,
                        }}
                    >
                        {bookingAvailable ? "Выбрать время" : "Запросить время"}
                    </NewBookingPageLink>

                    {hasSession && (
                        <Button
                            className={style.buttonQuestion}
                            data-testid={`psy-profile-ask-psy-${dateTestIdPrefix}`}
                            onClick={onClickAskPsy}
                        >
                            Задать вопрос психологу
                        </Button>
                    )}
                </div>
            </Box>
        )
    }
)

Booking.displayName = "PsyBooking"
